import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { cn } from '@/shadcn/lib/utils';
import { PLATFORM_CONFIG } from './constants';

interface ProfileHandleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** The username/handle to display */
  handle: string;
  /** The platform to determine @ prefix logic */
  platform?: string;
  /** Optional click handler for opening profile */
  onProfileClick?: () => void;
  /** Optional custom className */
  className?: string;
}

/**
 * ProfileHandle
 * ---------------
 * A clickable handle/username component that displays platform-appropriate formatting
 * and handles profile navigation. Used across sheet-based components for consistent
 * handle display and interaction.
 *
 * Platform Logic:
 *  - Instagram, Pinterest, TikTok: Always show @ prefix
 *  - YouTube, Facebook, others: No @ prefix
 *
 * Design tokens & classes:
 *  - Text size: text-sm (14px)
 *  - Font weight: font-medium (500)
 *  - Default color: text-gray-900
 *  - Hover color: text-blue-600
 *  - Hover underline: hover:underline
 *  - Cursor: cursor-pointer
 *  - Text alignment: text-left
 */
const ProfileHandle = forwardRef<HTMLButtonElement, ProfileHandleProps>(
  ({ handle, platform, onProfileClick, className, ...props }, ref) => {
    // Determine if @ prefix should be shown based on platform
    const shouldShowAtPrefix = PLATFORM_CONFIG.WITH_AT_PREFIX.includes(platform as any);

    // Format the handle with appropriate prefix
    const formattedHandle = shouldShowAtPrefix ? `@${handle}` : handle;

    return (
      <button
        ref={ref}
        onClick={onProfileClick}
        className={cn(
          'text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline cursor-pointer text-left',
          className
        )}
        {...props}
      >
        {formattedHandle}
      </button>
    );
  }
);

ProfileHandle.displayName = 'ProfileHandle';

export default ProfileHandle;
